// theme/assets/product-collection-trautis.js
// Trautis collection page — card hydration (compat badge + wishlist heart),
// BMW toggle, filter chips, load-more (incl. 8s-dwell auto-load cap-3).
//
// Phase 3a — first commit lands compat-hydration only. Subsequent tasks
// extend the same module additively (T5 wishlist, T9 toggle, T12 load-more).
//
// Public API on window.TrautisCollection (browser) / module.exports (Node).
(function () {
  'use strict';

  var BMW_KEY = 'trautis.mein_bmw';  // Phase 1d / 2a convention. Verified by pre-flight P6.

  function safeReadBmw() {
    try {
      var raw = (typeof localStorage !== 'undefined') ? localStorage.getItem(BMW_KEY) : null;
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function safeParseGids(card) {
    try { return JSON.parse(card.dataset.fitmentGids); }
    catch (e) { return []; }
  }

  function hydrateCompat() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return Promise.resolve();
    if (typeof window.TrautisFitment === 'undefined') return Promise.resolve();

    var bmw = safeReadBmw();
    if (!bmw) return Promise.resolve();

    var cards = document.querySelectorAll('.t-product-card[data-fitment-gids]');
    if (!cards.length) return Promise.resolve();

    return window.TrautisFitment.loadLookup().then(function (lookup) {
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        if (card.hasAttribute('data-trautis-bound-compat')) continue;
        card.setAttribute('data-trautis-bound-compat', '');
        var compat = card.querySelector('.t-product-card__compat');
        if (!compat) continue;
        var gids = safeParseGids(card);
        if (!gids.length) continue;
        var state = window.TrautisFitment.matches(gids, bmw, lookup);
        if (state === 'match') {
          compat.classList.add('t-product-card__compat--match');
          compat.removeAttribute('hidden');
        } else if (state === 'no-match') {
          compat.classList.add('t-product-card__compat--nomatch');
          compat.removeAttribute('hidden');
          // Kein Ausgrauen mehr: Das Dimmen nutzte dasselbe Treatment wie
          // ausverkaufte Produkte. Beim Stöbern (Fitment-Filter aus, BMW gesetzt)
          // sah jedes nicht passende Teil aus wie "nicht verfügbar". Das Badge
          // "Passt nicht" reicht als Orientierung; der harte Filter blendet aus.
        }
        // 'unknown' und 'engine-unconfirmed' → leave hidden, do nothing.
        // (engine-unconfirmed = „Alle Motoren" gewählt + motorspezifisches Teil:
        //  im Grid kein Badge — die Klärung übernimmt der PDP-Checker.)
      }
    }).catch(function () {
      // Silent failure per spec §6: badges stay hidden; no toast.
    });
  }

  // Wipe every card's compat verdict so the next hydrateCompat() pass starts
  // from a clean slate. Without this, hydrateCompat is additive-only and guarded
  // by data-trautis-bound-compat, so a vehicle change would never drop the
  // previous vehicle's grey-out (bug: stale until full page refresh).
  function clearCompatState() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    var cards = document.querySelectorAll('.t-product-card[data-fitment-gids]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      card.removeAttribute('data-trautis-bound-compat');
      card.classList.remove('t-product-card--incompat');
      var compat = card.querySelector('.t-product-card__compat');
      if (compat) {
        compat.classList.remove('t-product-card__compat--match');
        compat.classList.remove('t-product-card__compat--nomatch');
        compat.setAttribute('hidden', '');
      }
    }
  }

  // Re-evaluate all cards against the current vehicle. Called on
  // 'trautis:bmw-changed' so switching/clearing the vehicle updates the
  // grey-out live, without a page refresh.
  function recomputeCompat() {
    clearCompatState();
    return hydrateCompat();
  }

  function hydrateWishlist() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    if (typeof window.TrautisWishlist === 'undefined') return;

    var hearts = document.querySelectorAll('.t-product-card__wish');
    if (!hearts.length) return;

    function payloadFromHeart(heart) {
      var ds = heart.dataset;
      var variantId = parseInt(ds.variantId, 10);
      var priceCents = parseInt(ds.priceCents, 10);
      return {
        productHandle: ds.productHandle,
        variantId: isNaN(variantId) ? null : variantId,
        snapshot: {
          title: ds.title || '',
          vendor: ds.vendor || '',
          price: isNaN(priceCents) ? 0 : priceCents,
          currency: ds.currency || 'EUR',
          imageUrl: ds.imageUrl || '',
          productUrl: ds.productUrl || ''
        }
      };
    }

    function bind(heart) {
      if (heart.hasAttribute('data-trautis-bound-wish')) return;
      heart.setAttribute('data-trautis-bound-wish', '');
      heart.removeAttribute('disabled');
      var p = payloadFromHeart(heart);
      var initial = window.TrautisWishlist.isInList(p.productHandle, p.variantId);
      heart.setAttribute('aria-pressed', initial ? 'true' : 'false');
      heart.addEventListener('click', function (e) {
        if (e && e.preventDefault) e.preventDefault();
        var nowIn = window.TrautisWishlist.toggle(payloadFromHeart(heart));
        heart.setAttribute('aria-pressed', nowIn ? 'true' : 'false');
      });
    }

    for (var i = 0; i < hearts.length; i++) bind(hearts[i]);
  }

  // Flag: user clicked toggle "to turn on" before any BMW was set. The click
  // opens the BMW selector modal; once the user picks a car (trautis:bmw-changed
  // fires → hydrateBmwToggle re-runs), we consume the flag and apply the
  // fitment filter automatically. Honors the intent "I want to filter for my car".
  var pendingToggleApply = false;

  function applyFitmentFromBmw(bmw) {
    if (!window.TrautisFitment || !window.TrautisFitment.resolveFilterIds) return;
    window.TrautisFitment.resolveFilterIds(bmw.chassis, bmw.engine).then(function (gids) {
      if (!gids || !gids.length) return;
      // Apply ALL resolved GIDs as repeated filter values, not just gids[0].
      // resolveFilterIds returns the union [engine, 'any'] — Shopify ORs repeated
      // metafield filter values, so this matches every product the PDP compat-checker
      // considers compatible (incl. parts carrying only the chassis-wide "any" app).
      // Using gids[0] alone wrongly hid those (e.g. F23+B58 → the F23-any turbos).
      // Mirrors TrautisFitment.appendFilterToUrl used by the nav interceptor.
      var url = new URL(window.location.href);
      url.searchParams.delete('filter.p.m.custom.fitment_applications');
      var seen = {};
      gids.forEach(function (gid) {
        if (!seen[gid]) { seen[gid] = true; url.searchParams.append('filter.p.m.custom.fitment_applications', gid); }
      });
      window.location.assign(url.toString());
    });
  }

  // Idempotent: safe to re-run on 'trautis:bmw-changed'. Toggle is always
  // visible; aria-pressed reflects whether the fitment URL filter is active.
  // Click handler binds only once.
  function hydrateBmwToggle() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    // Es kann MEHRERE Toggles geben: Desktop-Sidebar, mobiles Filter-Sheet und
    // die mobile Chrome-Pill rendern dieselbe Komponente. querySelectorAll (statt
    // querySelector) bindet alle — vorher war der mobile Toggle deshalb tot.
    var toggles = document.querySelectorAll('[data-bmw-toggle]');
    if (!toggles.length) return;

    var bmw = safeReadBmw();
    var hasFitmentInUrl = (window.location.search || '').indexOf('filter.p.m.custom.fitment_applications=') !== -1;

    // Consume pending "switch on" intent after the user picks a car in the modal.
    // Einmalig, unabhängig von der Toggle-Anzahl. applyFitmentFromBmw navigiert
    // asynchron (oder gar nicht, falls keine Fitment-IDs) — wir binden die Toggles
    // unten trotzdem, damit aria-pressed/Click in jedem Fall korrekt sind.
    if (bmw && pendingToggleApply) {
      pendingToggleApply = false;
      applyFitmentFromBmw(bmw);
    }

    for (var i = 0; i < toggles.length; i++) {
      (function (toggle) {
        toggle.removeAttribute('hidden');
        toggle.setAttribute('aria-pressed', (bmw && hasFitmentInUrl) ? 'true' : 'false');

        if (toggle.dataset.bmwToggleBound === '1') return;
        toggle.dataset.bmwToggleBound = '1';

        toggle.addEventListener('click', function (e) {
          if (e && e.preventDefault) e.preventDefault();
          var currentBmw = safeReadBmw();
          if (!currentBmw) {
            // No car yet → open the BMW selector modal (same flow as landing page).
            // Phase 1d convention: delegated click on any [data-action="open-bmw-selector"]
            // (header pill, drawer pill, hero CTA …) opens t-bmw-modal.
            pendingToggleApply = true;
            var openTrigger = document.querySelector('[data-action="open-bmw-selector"]');
            if (openTrigger && typeof openTrigger.click === 'function') openTrigger.click();
            return;
          }
          var currentlyOn = toggle.getAttribute('aria-pressed') === 'true';
          if (currentlyOn) {
            // Strip the fitment filter param from URL and reload.
            var url = new URL(window.location.href);
            var keys = [];
            url.searchParams.forEach(function (_, k) { if (k.indexOf('filter.p.m.custom.fitment_applications') === 0) keys.push(k); });
            keys.forEach(function (k) { url.searchParams.delete(k); });
            window.location.assign(url.toString());
          } else {
            applyFitmentFromBmw(currentBmw);
          }
        });
      })(toggles[i]);
    }
  }

  function applySort(value) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    window.location.assign(url.toString());
  }

  function hydrateSort() {
    if (typeof document === 'undefined' || !document.querySelector) return;
    var sel = document.querySelector('[data-sort-by]');
    if (!sel) return;
    sel.addEventListener('change', function () { applySort(sel.value); });
  }

  // Replace the native <select> sort with a styled dropdown matching the listing
  // design — the native open option list is OS-drawn and unstyleable. Pure
  // progressive enhancement: the native <select> stays in the DOM as the
  // noscript fallback + data source; if anything here bails, the styled native
  // pill (§43) remains usable. ARIA listbox semantics + keyboard nav preserved.
  function hydrateSortDropdown() {
    if (typeof document === 'undefined' || !document.querySelector || !document.createElement) return;
    var sel = document.querySelector('[data-sort-by]');
    if (!sel || !sel.options || !sel.options.length) return;
    var host = (sel.closest && sel.closest('.t-collection-chrome__sort')) || sel.parentNode;
    if (!host || host.getAttribute('data-sort-enhanced') === '') return;
    host.setAttribute('data-sort-enhanced', '');

    var options = [];
    for (var i = 0; i < sel.options.length; i++) {
      options.push({ value: sel.options[i].value, label: sel.options[i].text });
    }
    function indexOfValue(v) {
      for (var k = 0; k < options.length; k++) { if (options[k].value === v) return k; }
      return 0;
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 't-sort__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Sortierung wählen');
    var btnLabel = document.createElement('span');
    btnLabel.className = 't-sort__btn-label';
    btnLabel.textContent = options[indexOfValue(sel.value)].label;
    var chev = document.createElement('span');
    chev.className = 't-sort__chev';
    chev.setAttribute('aria-hidden', 'true');
    btn.appendChild(btnLabel);
    btn.appendChild(chev);

    var list = document.createElement('div');
    list.className = 't-sort__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    var optEls = [];
    for (var j = 0; j < options.length; j++) {
      (function (opt) {
        var item = document.createElement('div');
        item.className = opt.value === sel.value ? 't-sort__opt is-active' : 't-sort__opt';
        item.setAttribute('role', 'option');
        item.setAttribute('tabindex', '-1');
        item.setAttribute('aria-selected', opt.value === sel.value ? 'true' : 'false');
        var lab = document.createElement('span');
        lab.className = 't-sort__opt-label';
        lab.textContent = opt.label;
        var ck = document.createElement('span');
        ck.className = 't-sort__opt-check';
        ck.setAttribute('aria-hidden', 'true');
        ck.textContent = '✓';
        item.appendChild(lab);
        item.appendChild(ck);
        item.addEventListener('click', function () { choose(opt.value); });
        optEls.push(item);
        list.appendChild(item);
      })(options[j]);
    }

    var wrap = document.createElement('span');
    wrap.className = 't-sort__ui';
    wrap.appendChild(btn);
    wrap.appendChild(list);

    sel.className = (sel.className ? sel.className + ' ' : '') + 't-sort__native';
    sel.setAttribute('aria-hidden', 'true');
    sel.setAttribute('tabindex', '-1');
    host.appendChild(wrap);

    var isOpen = false;
    var focusIdx = indexOfValue(sel.value);

    function highlight(i) {
      if (i < 0) i = 0;
      if (i > optEls.length - 1) i = optEls.length - 1;
      focusIdx = i;
      for (var n = 0; n < optEls.length; n++) { optEls[n].classList.toggle('is-focus', n === i); }
      if (optEls[i] && optEls[i].focus) optEls[i].focus();
    }
    function setOpen(v) {
      isOpen = v;
      list.hidden = !v;
      btn.setAttribute('aria-expanded', v ? 'true' : 'false');
      btn.classList.toggle('is-open', v);
      if (v) {
        highlight(indexOfValue(sel.value));
        document.addEventListener('click', onDocClick, true);
        document.addEventListener('keydown', onKeydown, true);
      } else {
        document.removeEventListener('click', onDocClick, true);
        document.removeEventListener('keydown', onKeydown, true);
      }
    }
    function choose(value) {
      if (value === sel.value) { setOpen(false); if (btn.focus) btn.focus(); return; }
      applySort(value);
    }
    function onDocClick(e) {
      var t = e && e.target;
      if (t && wrap.contains && wrap.contains(t)) return;
      setOpen(false);
    }
    function onKeydown(e) {
      var key = e.key;
      if (key === 'Escape' || key === 'Esc') { setOpen(false); if (btn.focus) btn.focus(); return; }
      if (key === 'ArrowDown' || key === 'Down') { if (e.preventDefault) e.preventDefault(); highlight(focusIdx + 1); }
      else if (key === 'ArrowUp' || key === 'Up') { if (e.preventDefault) e.preventDefault(); highlight(focusIdx - 1); }
      else if (key === 'Home') { if (e.preventDefault) e.preventDefault(); highlight(0); }
      else if (key === 'End') { if (e.preventDefault) e.preventDefault(); highlight(optEls.length - 1); }
      else if (key === 'Enter' || key === ' ' || key === 'Spacebar') { if (e.preventDefault) e.preventDefault(); choose(options[focusIdx].value); }
    }
    btn.addEventListener('click', function () { setOpen(!isOpen); });
    btn.addEventListener('keydown', function (e) {
      var key = e.key;
      if (!isOpen && (key === 'ArrowDown' || key === 'Down' || key === 'Enter' || key === ' ' || key === 'Spacebar')) {
        if (e.preventDefault) e.preventDefault();
        setOpen(true);
      }
    });
  }

  function hydrateChips() {
    if (typeof document === 'undefined') return;
    var chips = document.querySelectorAll('[data-chip-param-name]');
    if (!chips.length) return;
    for (var i = 0; i < chips.length; i++) {
      (function (chip) {
        chip.addEventListener('click', function (e) {
          if (e && e.preventDefault) e.preventDefault();
          var name = chip.dataset.chipParamName;
          var val = chip.dataset.chipParamValue;
          var url = new URL(window.location.href);
          // Remove the specific value of this filter param (multi-value support).
          var current = url.searchParams.getAll(name);
          url.searchParams.delete(name);
          current.filter(function (v) { return v !== val; }).forEach(function (v) { url.searchParams.append(name, v); });
          window.location.assign(url.toString());
        });
      })(chips[i]);
    }
  }

  function applyBmwContextToCount() {
    if (typeof document === 'undefined' || !document.querySelector) return;
    var hasFitment = (window.location.search || '').indexOf('filter.p.m.custom.fitment_applications=') !== -1;
    if (!hasFitment) return;
    var count = document.querySelector('[data-result-count]');
    if (!count) return;
    count.textContent = count.textContent.trim() + ' passend zu deinem BMW';
  }

  var DEFAULT_DWELL_MS = 8000;
  var AUTOLOAD_CAP = 3;
  var dwellMs = DEFAULT_DWELL_MS;

  // Skeleton-Platzhalter für den OOS-lastigen Init-Fill. Spiegelt die
  // .t-product-card-Form (Quadrat-Media + Marke/Titel/Preis-Zeilen), trägt aber
  // bewusst NICHT die Klasse .t-product-card — sonst zählte die Reihen-Mathematik
  // (appendFullRows) und die Karten-Hydration die leeren Kacheln mit.
  function buildSkeletonCard() {
    if (typeof document === 'undefined' || !document.createElement) return null;
    var el = document.createElement('div');
    el.className = 't-card-skel';
    el.setAttribute('aria-hidden', 'true');
    var media = document.createElement('div');
    media.className = 't-card-skel__media';
    el.appendChild(media);
    var body = document.createElement('div');
    body.className = 't-card-skel__body';
    var lines = ['brand', 'title', 'title2', 'price'];
    for (var i = 0; i < lines.length; i++) {
      var line = document.createElement('span');
      line.className = 't-card-skel__line t-card-skel__line--' + lines[i];
      body.appendChild(line);
    }
    el.appendChild(body);
    return el;
  }

  function hydrateLoadMore() {
    if (typeof document === 'undefined' || !document.querySelector) return;
    var btn = document.querySelector('[data-loadmore]');
    var grid = document.querySelector('[data-grid]');
    if (!btn || !grid) return;

    var inFlight = false;
    var autoLoadCount = 0;
    var retryFails = 0;
    var observer = null;
    var pendingTimer = null;
    var hardStop = false;

    // Mobile (≤749px, Theme-Breakpoint) bekommt echtes Infinite Scroll:
    // sofort nachladen sobald der Button in Reichweite ist (kein Dwell) und
    // kein Auto-Load-Deckel. Desktop behält das bewusste 8s-Dwell + Cap-3,
    // damit auf großen Screens nicht ungewollt die ganze Collection lädt.
    var isMobile = (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
      ? window.matchMedia('(max-width: 749px)').matches
      : false;
    var effectiveDwellMs = isMobile ? 0 : dwellMs;
    var effectiveCap = isMobile ? Infinity : AUTOLOAD_CAP;

    // Determine starting page from current URL (?page=N), default to 1.
    // Track loaded-pages-ahead in closure so we don't depend on Liquid's
    // paginate.next.url (broken on this Shopify version — returns total-pages
    // of UNFILTERED collection, e.g. page=12 when filter narrowed to 2 pages).
    var startPage = parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10);
    var loadedAhead = 0;

    // Per-page bucket size; defaults to 24 to match `collection.cards.liquid`
    // (the `?view=cards` template). Compared against actual card count to
    // detect "this IS the last page" without needing an empty-response click.
    var perPage = parseInt(btn.getAttribute('data-per-page') || '24', 10);
    if (!(perPage > 0)) perPage = 24;

    // OOS-last gilt nur für Collections (data-oos-last am Grid). Die Suche
    // teilt Markup + dieses JS, behält aber ihre Relevanz-Reihenfolge.
    var oosLast = grid.hasAttribute('data-oos-last');

    // oosQueue: komplett ausverkaufte Karten warten außerhalb des DOM, bis das
    // echte Listenende erreicht ist — sichtbar im Grid würde der Soldout-
    // Schwanz bei jedem Nachladen erneut nach unten "wandern".
    // availBuffer: verfügbare Karten, die zurückgehalten werden, damit die
    // sichtbaren Reihen voll bleiben (keine "leeren Kacheln" unten rechts,
    // wenn ein Batch Ausverkaufte enthielt). SSR rundet dafür per
    // <template data-deferred-cards> ab; das JS führt das beim Nachladen fort.
    var oosQueue = [];
    var availBuffer = [];

    // Muss zur Liquid-Rechnung in main-collection-trautis passen:
    // kgV aus Desktop- und Mobile-Spaltenzahl (Mobile ist 1 oder 2).
    var colsD = parseInt(getComputedStyle(grid).getPropertyValue('--cols-d'), 10) || 4;
    var colsM = parseInt(getComputedStyle(grid).getPropertyValue('--cols-m'), 10) || 2;
    var ROW_LCM = colsD % colsM === 0 ? colsD : colsD * colsM;

    function hydrateNewCards() {
      hydrateCompat();
      hydrateWishlist();
      hydrateCardGallery();
    }

    // Skeleton-Platzhalter (nur Init-Fill): füllen die noch leeren Grid-Plätze
    // sofort mit grauen Kacheln, damit die Einstiegsseite einer OOS-lastigen
    // Collection (z. B. CSR: 1 lieferbar von 24) nicht als „1 Produkt → Spinner →
    // Nachtrickeln" erscheint. Reale Karten ersetzen die Skeletons Platz für Platz.
    var skeletons = [];
    function showSkeletons(count) {
      for (var i = 0; i < count; i++) {
        var sk = buildSkeletonCard();
        if (!sk) break;
        skeletons.push(sk);
        grid.appendChild(sk);
      }
    }
    function clearSkeletons() {
      for (var i = 0; i < skeletons.length; i++) {
        var sk = skeletons[i];
        if (sk.parentNode) sk.parentNode.removeChild(sk);
      }
      skeletons = [];
    }
    // Eine reale Karte einsetzen: ersetzt — falls vorhanden — den ersten noch
    // stehenden Skeleton an genau dessen Platz (kein Layout-Sprung), sonst
    // schlicht anhängen. Einziger Append-Pfad fürs Grid, damit Reihenfolge und
    // Skeleton-Bilanz stimmen.
    function placeRealCard(card) {
      if (skeletons.length) {
        var sk = skeletons.shift();
        grid.insertBefore(card, sk);
        grid.removeChild(sk);
      } else {
        grid.appendChild(card);
      }
    }

    // So viele Pufferkarten zeigen, dass die Reihen voll bleiben — NIE ragged
    // (keine "leere Kachel"). Reicht der Puffer nicht für eine volle Reihe,
    // passiert hier nichts (return 0) und die Weiterlade-Kette in performLoad
    // holt Nachschub. Gibt die Zahl der sichtbar gewordenen Karten zurück.
    function appendFullRows() {
      if (!availBuffer.length) return 0;
      var visible = grid.querySelectorAll('.t-product-card').length;
      var total = visible + availBuffer.length;
      var n = total - (total % ROW_LCM) - visible;
      if (n <= 0) return 0;
      var batch = availBuffer.splice(0, n);
      for (var b = 0; b < batch.length; b++) placeRealCard(batch[b]);
      return batch.length;
    }

    // Jeder "es lädt nichts mehr nach"-Pfad MUSS hierdurch (exhaust, leere
    // Antwort, Teil-Seite, Permanent-Error), sonst verschluckt die Queue
    // Produkte: erst die zurückgehaltenen verfügbaren Karten, dann gesammelt
    // die ausverkauften ans Ende.
    function flushAllPending() {
      var had = availBuffer.length + oosQueue.length;
      for (var f = 0; f < availBuffer.length; f++) placeRealCard(availBuffer[f]);
      for (var g = 0; g < oosQueue.length; g++) placeRealCard(oosQueue[g]);
      availBuffer = [];
      oosQueue = [];
      // Kein Nachladen mehr → etwaige Rest-Skeletons abräumen (Endzustand).
      clearSkeletons();
      // Geparkte Karten waren beim Batch-Hydrate noch nicht im DOM.
      if (had) hydrateNewCards();
    }

    function exhaustButton() {
      btn.classList.add('t-collection-loadmore--exhausted');
      btn.setAttribute('aria-disabled', 'true');
      hardStop = true;
      if (observer) { observer.disconnect(); observer = null; }
      flushAllPending();
    }

    // SSR-zurückgestellte Karten (Volle-Reihen-Überhang + Ausverkaufte der
    // Einstiegsseite) aus dem inerten Template in die Puffer übernehmen.
    // <template>-Inhalt lädt keine Bilder — erst beim Anhängen ans Grid.
    var deferredTpl = document.querySelector('template[data-deferred-cards]');
    var deferredCount = 0;
    if (deferredTpl) {
      var defCards = deferredTpl.content.querySelectorAll('.t-product-card');
      deferredCount = defCards.length;
      for (var s = 0; s < defCards.length; s++) {
        if (defCards[s].classList.contains('t-product-card--soldout')) oosQueue.push(defCards[s]);
        else availBuffer.push(defCards[s]);
      }
      deferredTpl.parentNode.removeChild(deferredTpl);
    }

    // SSR-gerenderte Skeleton-Platzhalter adoptieren: stehen schon beim ersten
    // Paint im Grid (kein „erst 1 Produkt, dann tauchen die Balken auf"-Versatz,
    // weil dieses defer-Skript nicht erst laufen muss). Werden hier ins selbe
    // skeletons[]-Register übernommen → placeRealCard ersetzt sie, clearSkeletons
    // (via flushAllPending/Exhaust) räumt sie ab. Erzeugt KEINE neuen.
    var ssrSkels = grid.querySelectorAll('.t-card-skel');
    for (var ss2 = 0; ss2 < ssrSkels.length; ss2++) skeletons.push(ssrSkels[ss2]);

    // Init exhaust: if SSR (grid + deferred template) already rendered fewer
    // cards than one full page, we're on the last page already (paginate.pages
    // over-reports under some filter combinations on this Shopify version).
    // Hide before user clicks; flush so the deferral can't swallow cards.
    if (grid.querySelectorAll('.t-product-card').length + deferredCount < perPage) {
      flushAllPending();
      btn.classList.add('t-collection-loadmore--exhausted');
      btn.setAttribute('aria-disabled', 'true');
      return;
    }

    // minShow: wie viele Karten dieser Vorgang (Klick/Dwell/Init-Fill) noch
    // sichtbar machen soll. Liefert eine Seite zu wenig Verfügbares (Rest in
    // Warteschlange/Puffer), kettet der Vorgang weiter — der Besucher bekommt
    // pro Klick immer eine "gefühlte Seite", egal wie OOS-lastig die Region ist.
    function performLoad(isAutoLoad, minShow) {
      if (typeof minShow !== 'number') minShow = perPage;
      if (inFlight || hardStop) return Promise.resolve();
      inFlight = true;
      btn.classList.remove('t-collection-loadmore--error');
      btn.classList.add('t-collection-loadmore--loading');
      btn.setAttribute('aria-busy', 'true');

      var pageToFetch = startPage + loadedAhead + 1;

      var url = new URL(window.location.href);
      url.searchParams.set('page', String(pageToFetch));
      url.searchParams.set('view', 'cards');

      return fetch(url.toString())
        .then(function (resp) {
          if (!resp.ok) throw new Error('HTTP ' + resp.status);
          return resp.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelectorAll('.t-product-card');

          // Empty response = no more products → exhaust the button so we
          // don't keep trying. Most common cause: Liquid's pagination.pages
          // was wrong and we requested past the filtered collection's end.
          if (cards.length === 0) {
            exhaustButton();
            inFlight = false;
            btn.classList.remove('t-collection-loadmore--loading');
            btn.removeAttribute('aria-busy');
            return;
          }

          // Collections: verfügbare Karten in den Puffer, ausverkaufte in die
          // Warteschlange; sichtbar wird reihenweise aus dem Puffer (volle
          // Reihen). Suche: schlichtes Anhängen wie eh und je.
          var shownNow;
          if (oosLast) {
            for (var i = 0; i < cards.length; i++) {
              if (cards[i].classList.contains('t-product-card--soldout')) {
                oosQueue.push(cards[i]);
              } else {
                availBuffer.push(cards[i]);
              }
            }
            shownNow = appendFullRows();
          } else {
            for (var j = 0; j < cards.length; j++) placeRealCard(cards[j]);
            shownNow = cards.length;
          }

          // Re-run hydration on newly added cards.
          hydrateNewCards();

          loadedAhead++;

          // Update the visible href so right-click → copy-link gives the
          // user a meaningful URL to the next batch.
          var hrefUrl = new URL(window.location.href);
          hrefUrl.searchParams.set('page', String(pageToFetch + 1));
          btn.setAttribute('href', hrefUrl.pathname + hrefUrl.search);

          retryFails = 0;
          if (isAutoLoad) autoLoadCount++;
          if (autoLoadCount >= effectiveCap && observer) {
            observer.disconnect();
            observer = null;
          }
          inFlight = false;
          btn.classList.remove('t-collection-loadmore--loading');
          btn.removeAttribute('aria-busy');

          // Partial-page → this WAS the last page. Exhaust without forcing
          // the user to click an empty fetch first.
          if (cards.length < perPage) {
            exhaustButton();
          } else if (oosLast && shownNow < minShow) {
            // Kette läuft weiter — Skeletons bleiben als Platzhalter stehen.
            // Zu wenig sichtbar geworden — Seite war (teils) ausverkauft,
            // der Rest liegt in Warteschlange/Puffer: direkt weiterladen,
            // sonst wirkt der Klick wirkungslos oder mickrig (z. B. +1 Karte
            // auf OOS-lastigen Collections wie exterior). Die Kette endet
            // spätestens an der Teil-/Leerseite (exhaust → flushAllPending
            // zeigt alles).
            return performLoad(isAutoLoad, minShow - shownNow);
          } else {
            // Fill-Ziel erreicht (oder normaler Klick fertig) → etwaige
            // Init-Fill-Skeletons abräumen; das Grid ist gefüllt.
            clearSkeletons();
          }
        })
        .catch(function () {
          inFlight = false;
          btn.classList.remove('t-collection-loadmore--loading');
          btn.removeAttribute('aria-busy');
          if (isAutoLoad) {
            // Auto-load-Fehler: Cap nicht verbrennen. Aber etwaige Init-Fill-
            // Skeletons nicht ewig stehen lassen — abräumen, Sichtbares bleibt.
            clearSkeletons();
            return;
          }
          retryFails++;
          if (retryFails >= 3) {
            btn.classList.add('t-collection-loadmore--permanent-error');
            hardStop = true;
            if (observer) { observer.disconnect(); observer = null; }
            // Kein Nachladen mehr möglich → geparkte Karten nicht
            // verschlucken, sondern ans Ende des Geladenen anhängen.
            flushAllPending();
          } else {
            btn.classList.add('t-collection-loadmore--error');
          }
        });
    }

    btn.addEventListener('click', function (e) {
      if (e && e.preventDefault) e.preventDefault();
      performLoad(false);
    });

    // OOS-lastige Einstiegsseite (weniger als zwei volle Reihen verfügbar,
    // z. B. exterior: 3 von 24) → sofort bis zu einer gefühlten Seite
    // auffüllen statt auf Klick/Dwell zu warten. Normale Collections
    // (SSR-gerundet ≥ 2 Reihen) laden NICHT eager.
    var initVisible = grid.querySelectorAll('.t-product-card').length;
    if (oosLast && initVisible < ROW_LCM * 2) {
      // SSR liefert die Skeletons normalerweise schon mit (sofort sichtbar).
      // Nur falls keine adoptiert wurden (alte SSR-Version / Cache), hier per
      // JS nachziehen, DANN nachladen — die ankommenden Karten ersetzen sie.
      if (!skeletons.length) showSkeletons(perPage - initVisible);
      performLoad(true, perPage - initVisible);
    }

    if (typeof IntersectionObserver === 'undefined') return;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          if (pendingTimer) clearTimeout(pendingTimer);
          pendingTimer = setTimeout(function () {
            performLoad(true);
          }, effectiveDwellMs);
        } else {
          if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
        }
      });
      // Auf Mobile schon 600px vor dem Button vorladen → nahtloses Scrollen
      // ohne sichtbares Stocken. Desktop feuert exakt beim Button (Default 0).
    }, isMobile ? { rootMargin: '600px 0px' } : undefined);
    observer.observe(btn);
  }

  function isGalleryDesktop() {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function promoteGalleryImg(img) {
    if (!img || !img.dataset || !img.dataset.src) return;
    img.setAttribute('src', img.dataset.src);
    if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  // Desktop-only image gallery on collection cards: hover swaps to image 2,
  // arrows cycle through product.images (wrap-around). Bild 2 is preloaded when
  // the card enters the viewport; images 3..N load on first hover. Idempotent
  // (data-gallery-bound) so load-more re-hydration only binds NEW cards.
  function hydrateCardGallery() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    if (!isGalleryDesktop()) return;

    var cards = document.querySelectorAll('.t-product-card[data-gallery]');
    if (!cards.length) return;

    // One observer per hydration pass, created lazily so an all-already-bound
    // re-hydration (load-more) makes none. It preloads image 2 when a card
    // scrolls into view, then unobserves; once every observed card has entered
    // it disconnects so idle observers don't accumulate across load-more passes.
    var io = null;
    var ioPending = 0;
    function ensureIO() {
      if (io || typeof IntersectionObserver === 'undefined') return io;
      io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var imgs = entry.target.querySelectorAll('.t-product-card__gallery-img');
          if (imgs[1]) promoteGalleryImg(imgs[1]); // preload image 2
          obs.unobserve(entry.target);
          ioPending--;
          if (ioPending <= 0) obs.disconnect();
        });
      });
      return io;
    }

    function bind(card) {
      if (card.hasAttribute('data-gallery-bound')) return;

      var imgs = card.querySelectorAll('.t-product-card__gallery-img');
      if (!imgs || imgs.length < 2) return;

      // Stamp only after the card is confirmed gallery-capable, so a stray
      // data-gallery on a 1-image card isn't marked bound-forever with no UI.
      card.setAttribute('data-gallery-bound', '');

      var prev = card.querySelector('.t-product-card__nav--prev');
      var next = card.querySelector('.t-product-card__nav--next');
      var n = imgs.length;
      var index = 0;
      var restHydrated = false;

      function setActive(i) {
        if (imgs[index]) imgs[index].classList.remove('is-active');
        index = (i + n) % n;
        if (imgs[index]) imgs[index].classList.add('is-active');
      }
      function hydrateRest() {
        if (restHydrated) return;
        for (var k = 1; k < n; k++) promoteGalleryImg(imgs[k]);
        restHydrated = true;
      }

      card.addEventListener('mouseenter', function () {
        hydrateRest();
        setActive(1); // show second image on hover
      });
      card.addEventListener('mouseleave', function () {
        setActive(0);
      });
      if (prev) prev.addEventListener('click', function (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        hydrateRest();
        setActive(index - 1);
      });
      if (next) next.addEventListener('click', function (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        hydrateRest();
        setActive(index + 1);
      });

      var obs = ensureIO();
      if (obs) { obs.observe(card); ioPending++; }
    }

    for (var i = 0; i < cards.length; i++) bind(cards[i]);
  }

  var VF_FILTER_PREFIX = 'filter.p.m.custom.fitment_applications';
  var VF_REMOVED_KEY = 'trautis.vf_removed';

  // Safety-Net: aktiver Fitment-Filter auf einer nicht-fahrzeugspezifischen,
  // leergefilterten Collection → Filter strippen und sauber neu laden.
  // Gibt true zurück, wenn ein Reload ausgelöst wurde (init bricht dann ab).
  function guardIrrelevantFitmentFilter() {
    if (typeof window === 'undefined' || !window.location) return false;
    if (typeof document === 'undefined' || !document.querySelector) return false;
    var search = window.location.search || '';
    if (search.indexOf(VF_FILTER_PREFIX + '=') === -1) return false;
    var root = document.querySelector('[data-trautis-collection]');
    if (!root) return false;
    if (root.getAttribute('data-fitment-relevant') !== 'false') return false;
    // data-page-items = paginate.items = sicher die GEFILTERTE Anzahl (data-total/
    // products_count hat unklare Filter-Semantik). "0" = Seite ist leergefiltert.
    if (root.getAttribute('data-page-items') !== '0') return false;

    var url = new URL(window.location.href);
    var keys = [];
    url.searchParams.forEach(function (_, k) {
      if (k.indexOf(VF_FILTER_PREFIX) === 0) keys.push(k);
    });
    keys.forEach(function (k) { url.searchParams.delete(k); });
    try { sessionStorage.setItem(VF_REMOVED_KEY, '1'); } catch (e) {}
    // replace statt assign: der Auto-Strip ist keine Nutzeraktion; die kaputte
    // gefilterte URL darf nicht in der History bleiben (Zurück-Button-Falle).
    window.location.replace(url.toString());
    return true;
  }

  // Einmaliger Hinweis nach dem Auto-Strip-Reload.
  function showVehicleFilterRemovedNotice() {
    if (typeof document === 'undefined' || !document.querySelector) return;
    var flag = null;
    try { flag = sessionStorage.getItem(VF_REMOVED_KEY); } catch (e) {}
    if (!flag) return;
    try { sessionStorage.removeItem(VF_REMOVED_KEY); } catch (e) {}
    var el = document.querySelector('[data-vehicle-filter-removed]');
    if (el) el.removeAttribute('hidden');
  }

  function sameGidSet(a, b) {
    if (a.length !== b.length) return false;
    var seen = {};
    b.forEach(function (x) { seen[x] = true; });
    for (var i = 0; i < a.length; i++) { if (!seen[a[i]]) return false; }
    return true;
  }

  // Garage-wins: lädt eine FAHRZEUGSPEZIFISCHE Collection mit einem bereits
  // AKTIVEN Fitment-Filter, dessen Fahrzeug NICHT zum gespeicherten Garagen-
  // Fahrzeug passt (geteilter Link, stale History), schreibt der Filter aufs
  // Garagen-Fahrzeug um und lädt sauber neu — so stimmen Grid, Header-Pill und
  // PDP-Compat-Checker wieder überein (alle drei beziehen sich aufs Garagen-Auto).
  // Wichtig: greift NUR bei bereits aktivem Filter (führt NIE selbst einen Filter
  // ein → respektiert das explizite „Anwenden"), nur bei echtem Mismatch (sonst
  // Reload-Loop) und nur auf fahrzeugspezifischen Collections (Nicht-Fahrzeug
  // regelt guardIrrelevantFitmentFilter). Gibt Promise<boolean> (true = Reload).
  function reconcileFitmentFilterToGarage() {
    if (typeof window === 'undefined' || !window.location) return Promise.resolve(false);
    if (typeof document === 'undefined' || !document.querySelector) return Promise.resolve(false);
    var search = window.location.search || '';
    if (search.indexOf(VF_FILTER_PREFIX + '=') === -1) return Promise.resolve(false);
    var root = document.querySelector('[data-trautis-collection]');
    if (!root) return Promise.resolve(false);
    if (root.getAttribute('data-fitment-relevant') !== 'true') return Promise.resolve(false);
    var bmw = safeReadBmw();
    if (!bmw || !bmw.chassis || !bmw.engine) return Promise.resolve(false);
    if (!window.TrautisFitment || !window.TrautisFitment.resolveFilterIds) return Promise.resolve(false);

    var url = new URL(window.location.href);
    var urlGids = [];
    url.searchParams.forEach(function (v, k) {
      if (k.indexOf(VF_FILTER_PREFIX) === 0) urlGids.push(v);
    });
    return window.TrautisFitment.resolveFilterIds(bmw.chassis, bmw.engine).then(function (gids) {
      if (!gids || !gids.length) return false;
      // Schon konsistent (normaler Toggle-/Nav-Flow) → nichts tun, kein Reload-Loop.
      if (sameGidSet(urlGids, gids)) return false;
      url.searchParams.delete(VF_FILTER_PREFIX);
      var seen = {};
      gids.forEach(function (gid) {
        if (!seen[gid]) { seen[gid] = true; url.searchParams.append(VF_FILTER_PREFIX, gid); }
      });
      // replace statt assign: keine Nutzeraktion; die fremde gefilterte URL
      // gehört nicht in die History (Zurück-Button-Falle).
      window.location.replace(url.toString());
      return true;
    });
  }

  // BMW-aware Leerzustand. Das SSR-`data-has-fitment` ist im echten 0-Treffer-Fall
  // unzuverlässig (Shopify-`active_values` ist leer, wenn die Fahrzeug-GID gar kein
  // Facet-Value der Collection ist). Verlässliches Signal = Fitment-Param in der URL.
  // Ist er aktiv: den „BMW-Filter entfernen"-Button einblenden (entfernt nur das
  // Auto, andere Filter bleiben). Ist der Fitment-Filter der EINZIGE aktive Filter
  // (`data-has-other="false"`), zusätzlich die Leermeldung auf die BMW-Botschaft
  // umschalten — bei Kombi-Filtern (z. B. + Preis) bliebe „keine Teile für deinen
  // BMW" irreführend, dort bleibt die generische Meldung stehen.
  function hydrateEmptyStateBmwRemoval() {
    if (typeof document === 'undefined' || !document.querySelector) return;
    var empty = document.querySelector('[data-empty-state]');
    if (!empty) return;
    if ((window.location.search || '').indexOf(VF_FILTER_PREFIX + '=') === -1) return;
    var btn = empty.querySelector('[data-empty-remove-bmw]');
    if (btn) {
      btn.removeAttribute('hidden');
      btn.addEventListener('click', function () {
        var url = new URL(window.location.href);
        var toDelete = [];
        url.searchParams.forEach(function (_, k) {
          if (k.indexOf(VF_FILTER_PREFIX) === 0) toDelete.push(k);
        });
        toDelete.forEach(function (k) { url.searchParams.delete(k); });
        window.location.assign(url.toString());
      });
    }
    if (empty.getAttribute('data-has-other') === 'false') {
      var title = empty.querySelector('[data-empty-title]');
      var bmwTitle = empty.getAttribute('data-empty-title-bmw');
      if (title && bmwTitle) title.textContent = bmwTitle;
    }
  }

  var api = {
    hydrateCompat: hydrateCompat,
    clearCompatState: clearCompatState,
    recomputeCompat: recomputeCompat,
    hydrateWishlist: hydrateWishlist,
    hydrateCardGallery: hydrateCardGallery,
    hydrateBmwToggle: hydrateBmwToggle,
    hydrateSort: hydrateSort,
    hydrateSortDropdown: hydrateSortDropdown,
    hydrateChips: hydrateChips,
    applyBmwContextToCount: applyBmwContextToCount,
    hydrateLoadMore: hydrateLoadMore,
    hydrateEmptyStateBmwRemoval: hydrateEmptyStateBmwRemoval,
    guardIrrelevantFitmentFilter: guardIrrelevantFitmentFilter,
    showVehicleFilterRemovedNotice: showVehicleFilterRemovedNotice,
    reconcileFitmentFilterToGarage: reconcileFitmentFilterToGarage,
    _setDwellMs: function (n) { dwellMs = n; }, // test hook
    _bmwKey: BMW_KEY
  };

  if (typeof window !== 'undefined') window.TrautisCollection = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  function init() {
    // Safety-Net zuerst: löst ggf. einen Reload aus → restliche Hydration
    // wäre verschwendet (die neue Seite hydratisiert frisch).
    if (guardIrrelevantFitmentFilter()) return;
    showVehicleFilterRemovedNotice();
    // Garage-wins: fremden/abweichenden Fitment-Filter aufs Garagen-Fahrzeug
    // umschreiben (async; löst ggf. einen sauberen Reload aus). Fire-and-forget —
    // die restliche Hydration darf laufen; bei Mismatch verwirft der Reload sie.
    reconcileFitmentFilterToGarage();
    hydrateCompat();
    hydrateWishlist();
    hydrateCardGallery();
    hydrateBmwToggle();
    hydrateSort();
    hydrateSortDropdown();
    hydrateChips();
    applyBmwContextToCount();
    hydrateLoadMore();
    hydrateEmptyStateBmwRemoval();
    // Live re-evaluation when the vehicle changes anywhere (selector modal,
    // header pill, ?bmw= deep-link). TrautisFitment.set/clear dispatch this.
    // hydrateBmwToggle also runs so the "Nur kompatible Teile" toggle appears
    // (or disappears) instantly without a hard refresh.
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('trautis:bmw-changed', recomputeCompat);
      document.addEventListener('trautis:bmw-changed', hydrateBmwToggle);
    }
  }
  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
